import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { AddedToCartList } from './added-to-cart-list';
import { BORDER_RADIUS_LARGE, shadow } from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import { MinimalScanCartList } from './minimal-scan-cart-list';
import { MinimalScanResults } from './minimal-scan-results';
import { PreScanComponent } from './pre-scan-component';
import { ScannerType } from './scanner-types';
import { ScanResults } from './scan-results';
import { ScanResultsLoading } from './scan-results-loading';
import { StyleProp, View, ViewStyle } from 'react-native';
import { useMaybeLocalScannerContext } from '@/contexts/local-scanner-context';
import { useScannerContext } from '@/contexts/scanner-context';

export interface Props {
  children?: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  scannerType: ScannerType;
}

enum ScanResultContentType {
  Cart = 'CART',
  Empty = 'EMPTY',
  Loading = 'LOADING',
  ShowResults = 'SHOW_RESULTS',
}

export const ScanResultsSheet: React.FC<Props> = ({
  children,
  containerStyle,
  scannerType,
}) => {
  const localScannerContext = useMaybeLocalScannerContext();
  const { cartVariantPacks, offDeviceSearchResults, onDeviceSearchResults } =
    useScannerContext();
  const [styles] = useStyles();

  const isLocalScan = localScannerContext !== null;

  const scanContentType = useMemo(() => {
    if (isLocalScan) {
      const { cartEntries, searchResults } = localScannerContext;

      if (
        searchResults &&
        searchResults.orderGuideResults.length +
          searchResults.catalogResults.length >
          1
      ) {
        return ScanResultContentType.ShowResults;
      }

      if (
        localScannerContext.offDeviceSearchResults &&
        localScannerContext.offDeviceSearchResults.length > 1
      ) {
        return ScanResultContentType.ShowResults;
      }

      if (!cartEntries) {
        return ScanResultContentType.Loading;
      }

      return cartEntries.length > 0
        ? ScanResultContentType.Cart
        : ScanResultContentType.Empty;
    }

    // Legacy path
    if (!cartVariantPacks) {
      return ScanResultContentType.Loading;
    }

    const hasMultipleOnDeviceResults =
      onDeviceSearchResults && onDeviceSearchResults.length > 1;
    const hasMultipleOffDeviceResults =
      offDeviceSearchResults && offDeviceSearchResults.length > 1;

    if (hasMultipleOnDeviceResults || hasMultipleOffDeviceResults) {
      return ScanResultContentType.ShowResults;
    }

    return cartVariantPacks.length > 0
      ? ScanResultContentType.Cart
      : ScanResultContentType.Empty;
  }, [
    cartVariantPacks,
    isLocalScan,
    localScannerContext,
    offDeviceSearchResults,
    onDeviceSearchResults,
  ]);

  const scanContentTypeMap: Record<ScanResultContentType, React.ReactNode> = {
    [ScanResultContentType.Cart]: isLocalScan ? (
      <MinimalScanCartList scannerType={scannerType} />
    ) : (
      <AddedToCartList scannerType={scannerType} />
    ),
    [ScanResultContentType.Empty]: <PreScanComponent />,
    [ScanResultContentType.Loading]: (
      <ScanResultsLoading
        message={i18n.t('external_scanner.loading_state')}
        style={
          scannerType === ScannerType.Camera ? styles.cameraLoading : undefined
        }
      />
    ),
    [ScanResultContentType.ShowResults]: isLocalScan ? (
      <MinimalScanResults scannerType={scannerType} />
    ) : (
      <ScanResults scannerType={scannerType} />
    ),
  };

  const showShadow =
    scanContentType === ScanResultContentType.ShowResults &&
    scannerType === ScannerType.External;

  return (
    <View style={[containerStyle, showShadow ? styles.shadow : null]}>
      {scanContentTypeMap[scanContentType]}
      {children}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  cameraLoading: {
    justifyContent: 'flex-start',
    marginTop: 80,
  },
  shadow: {
    ...shadow({
      color: theme.sk.color.bg.dark.default,
      elevation: 10,
      opacity: 0.15,
      radius: BORDER_RADIUS_LARGE,
      x: 0,
      y: -4,
    }),
    flex: 1,
  },
}));
